import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface ScaffoldResult {
  repoUrl: string;
  repoName: string;
  cloneCommand: string;
}

interface UseAppScaffolderResult {
  isScaffolding: boolean;
  error: string | null;
  expoResult: ScaffoldResult | null;
  nextResult: ScaffoldResult | null;
  scaffoldExpoApp: (projectId: string, config?: AppConfig) => Promise<ScaffoldResult | null>;
  scaffoldNextApp: (projectId: string, config?: AppConfig) => Promise<ScaffoldResult | null>;
  clearError: () => void;
  reset: () => void;
}

export interface AppConfig {
  features?: string[];
  includeAuth?: boolean;
  includeDatabase?: boolean;
  includeAnalytics?: boolean;
}

/**
 * Hook for scaffolding Expo and Next.js applications
 */
export function useAppScaffolder(): UseAppScaffolderResult {
  const [isScaffolding, setIsScaffolding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expoResult, setExpoResult] = useState<ScaffoldResult | null>(null);
  const [nextResult, setNextResult] = useState<ScaffoldResult | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setExpoResult(null);
    setNextResult(null);
  }, []);

  const scaffoldExpoApp = useCallback(
    async (projectId: string, config?: AppConfig): Promise<ScaffoldResult | null> => {
      if (!projectId) {
        setError("Project ID is required");
        return null;
      }

      setIsScaffolding(true);
      setError(null);

      try {
        const functions = getFunctions();
        const scaffoldFunction = httpsCallable<
          { projectId: string; appConfig?: AppConfig },
          { success: boolean; repoUrl: string; repoName: string; cloneCommand: string }
        >(functions, "scaffoldExpoApp");

        const result = await scaffoldFunction({ projectId, appConfig: config });

        const scaffoldResult: ScaffoldResult = {
          repoUrl: result.data.repoUrl,
          repoName: result.data.repoName,
          cloneCommand: result.data.cloneCommand,
        };

        setExpoResult(scaffoldResult);
        return scaffoldResult;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to scaffold Expo app";
        setError(errorMessage);
        return null;
      } finally {
        setIsScaffolding(false);
      }
    },
    []
  );

  const scaffoldNextApp = useCallback(
    async (projectId: string, config?: AppConfig): Promise<ScaffoldResult | null> => {
      if (!projectId) {
        setError("Project ID is required");
        return null;
      }

      setIsScaffolding(true);
      setError(null);

      try {
        const functions = getFunctions();
        const scaffoldFunction = httpsCallable<
          { projectId: string; appConfig?: AppConfig },
          { success: boolean; repoUrl: string; repoName: string; cloneCommand: string }
        >(functions, "scaffoldNextApp");

        const result = await scaffoldFunction({ projectId, appConfig: config });

        const scaffoldResult: ScaffoldResult = {
          repoUrl: result.data.repoUrl,
          repoName: result.data.repoName,
          cloneCommand: result.data.cloneCommand,
        };

        setNextResult(scaffoldResult);
        return scaffoldResult;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to scaffold Next.js app";
        setError(errorMessage);
        return null;
      } finally {
        setIsScaffolding(false);
      }
    },
    []
  );

  return {
    isScaffolding,
    error,
    expoResult,
    nextResult,
    scaffoldExpoApp,
    scaffoldNextApp,
    clearError,
    reset,
  };
}
